/**
 * Árvore à esquerda do explorer: empresa › organizações do topo › "Projetos" (a pasta
 * da plataforma fundida com as organizações da seção) › demais pastas da plataforma
 * (spec 2026-10-21-kb-raizes, §3 e §8). Pasta navegável e não liberada aparece (D4)
 * com ícone próprio, sem conteúdo.
 */
import { useEffect, useRef, useState } from 'react'
import { NavLink, Stack, Text } from '@mantine/core'
import {
  IconBuilding,
  IconBuildings,
  IconFolder,
  IconFolderOff,
  IconFolderOpen,
  IconFolders,
  IconLock,
} from '@tabler/icons-react'
import type { KbFolderNode } from '@/api/kb'
import { filhas, NOME_DO_NO_PROJETOS, type Empresa, type Raiz } from './arvore'
import { estiloDoAlvo, juntarRefs, useAlvoDeArquivo, useAlvoDeArraste, useOrigemDeArraste } from './ArrastarESoltar'

export type Selecao =
  | { tipo: 'raiz'; organizationId: number | null }
  | { tipo: 'pasta'; id: number }
  /** Empresa que não é a da plataforma (essa seleciona a raiz `organizationId: null`) */
  | { tipo: 'empresa'; raizId: number }

interface Props {
  empresas: Empresa[]
  nos: KbFolderNode[]
  selecao: Selecao | null
  /** Pastas no caminho da selecionada: ficam abertas */
  abertas: Set<number>
  onSelecionar: (s: Selecao) => void
}

function iconeDaPasta(no: KbFolderNode, aberta: boolean) {
  if (!no.liberada) return <IconFolderOff size={16} stroke={1.5} aria-label="sem acesso ao conteúdo" />
  return aberta ? <IconFolderOpen size={16} stroke={1.5} /> : <IconFolder size={16} stroke={1.5} />
}

function NoDaPasta({ no, nos, selecao, abertas, onSelecionar, visivel }: Omit<Props, 'empresas'> & { no: KbFolderNode; visivel: boolean }) {
  const sub = filhas(nos, no.id)
  const ativa = selecao?.tipo === 'pasta' && selecao.id === no.id
  // Aberta por estar no caminho da selecionada, até a pessoa recolher ou abrir à mão.
  const [manual, setManual] = useState<boolean | null>(null)
  const aberta = manual ?? (abertas.has(no.id) || ativa)
  const alvo = useAlvoDeArraste({ tipo: 'pasta', pasta: no }, visivel)
  const origem = useOrigemDeArraste({ tipo: 'pasta', pasta: no })
  const arquivo = useAlvoDeArquivo(no)
  const estado = arquivo.estado !== 'neutro' ? arquivo.estado : alvo.estado
  // Pasta recolhida abre sob o arraste depois de 600 ms (D7), para soltar numa filha.
  const sobArraste = estado !== 'neutro'
  useEffect(() => {
    if (!sobArraste || aberta || sub.length === 0) return
    const t = setTimeout(() => setManual(true), 600)
    return () => clearTimeout(t)
  }, [sobArraste, aberta, sub.length])
  // A árvore rola sozinha (#25437): a pasta aberta por link ou pela busca pode estar
  // fora da vista dela. `nearest` não mexe em nada quando já está visível.
  const linha = useRef<HTMLElement | null>(null)
  useEffect(() => {
    if (ativa) linha.current?.scrollIntoView?.({ block: 'nearest' })
  }, [ativa])
  return (
    <NavLink
      ref={juntarRefs(alvo.setNodeRef, origem.setNodeRef, (el) => { linha.current = el })}
      {...origem.attributes}
      {...origem.listeners}
      {...arquivo.handlers}
      style={{ ...estiloDoAlvo(estado), opacity: origem.isDragging ? 0.5 : undefined }}
      label={no.nome}
      active={ativa}
      opened={aberta}
      onChange={setManual}
      leftSection={iconeDaPasta(no, aberta)}
      rightSection={no.sensivel ? <IconLock size={12} stroke={1.5} aria-label="sensível" /> : undefined}
      c={no.liberada ? undefined : 'dimmed'}
      onClick={() => onSelecionar({ tipo: 'pasta', id: no.id })}
      childrenOffset={14}
    >
      {sub.length > 0
        ? sub.map((f) => (
            <NoDaPasta key={f.id} no={f} nos={nos} selecao={selecao} abertas={abertas} onSelecionar={onSelecionar} visivel={visivel && aberta} />
          ))
        : undefined}
    </NavLink>
  )
}

type Ramo = Omit<Props, 'empresas'> & {
  visivel: boolean
  /** Organização da seleção atual: abre o caminho até ela */
  orgDaSelecao: number | null | undefined
}

/**
 * A raiz da organização é alvo: soltar nela leva a pasta ao topo. Recolhida, as pastas
 * dela deixam de ser alvo. Dentro de "Projetos" com várias organizações, começa fechada.
 */
function RaizDaOrganizacao({ raiz, nos, selecao, abertas, onSelecionar, visivel, orgDaSelecao, abertaDePadrao = true }: Ramo & { raiz: Raiz; abertaDePadrao?: boolean }) {
  const alvo = useAlvoDeArraste({ tipo: 'raiz', organizationId: raiz.organizationId }, visivel)
  const [manual, setManual] = useState<boolean | null>(null)
  const aberta = manual ?? (abertaDePadrao || orgDaSelecao === raiz.organizationId)
  const ativa = selecao?.tipo === 'raiz' && selecao.organizationId === raiz.organizationId
  return (
    <NavLink
      ref={alvo.setNodeRef}
      style={estiloDoAlvo(alvo.estado)}
      label={<Text size="sm" fw={600}>{raiz.rotulo}</Text>}
      leftSection={<IconBuilding size={16} stroke={1.5} />}
      active={ativa}
      opened={aberta}
      onChange={setManual}
      onClick={() => onSelecionar({ tipo: 'raiz', organizationId: raiz.organizationId })}
      childrenOffset={10}
    >
      {raiz.pastas.length > 0
        ? raiz.pastas.map((p) => (
            <NoDaPasta key={p.id} no={p} nos={nos} selecao={selecao} abertas={abertas} onSelecionar={onSelecionar} visivel={visivel && aberta} />
          ))
        : undefined}
    </NavLink>
  )
}

/**
 * Nó "Projetos" (R3, R9): é a pasta da plataforma "Projetos" quando a pessoa a enxerga
 * — as subpastas dela (o ConsoleAI) vêm primeiro — e agrupa as organizações da seção.
 * Não é destino de arraste: agrupa escopos diferentes (§9). Fechado para quem tem mais
 * de uma organização nele, aberto para quem tem uma.
 */
function NoProjetos({ projetos, ...ramo }: Ramo & { projetos: NonNullable<Empresa['projetos']> }) {
  const { nos, selecao, abertas, onSelecionar, visivel, orgDaSelecao } = ramo
  const { pasta, organizacoes } = projetos
  const varias = organizacoes.length > 1
  const contem = (pasta !== null && abertas.has(pasta.id))
    || organizacoes.some((o) => o.organizationId === orgDaSelecao)
  const [manual, setManual] = useState<boolean | null>(null)
  const aberta = manual ?? (!varias || contem)
  const ativa = pasta !== null && selecao?.tipo === 'pasta' && selecao.id === pasta.id
  const sub = pasta ? filhas(nos, pasta.id) : []
  return (
    <NavLink
      label={pasta?.nome ?? NOME_DO_NO_PROJETOS}
      leftSection={<IconFolders size={16} stroke={1.5} />}
      active={ativa}
      opened={aberta}
      onChange={setManual}
      onClick={pasta ? () => onSelecionar({ tipo: 'pasta', id: pasta.id }) : undefined}
      childrenOffset={14}
    >
      {sub.map((f) => (
        <NoDaPasta key={f.id} no={f} nos={nos} selecao={selecao} abertas={abertas} onSelecionar={onSelecionar} visivel={visivel && aberta} />
      ))}
      {organizacoes.map((o) => (
        <RaizDaOrganizacao key={o.organizationId} raiz={o} {...ramo} visivel={visivel && aberta} abertaDePadrao={!varias} />
      ))}
    </NavLink>
  )
}

/**
 * A empresa (R1). A da plataforma é alvo como a raiz da plataforma era
 * (`EmpresaDaPlataforma`): soltar nela leva a pasta da plataforma ao topo. A seleção dela é a raiz `organizationId: null`, que
 * continua sendo onde se cria pasta da plataforma.
 */
function NoDaEmpresa({ empresa, alvo, ...ramo }: Omit<Ramo, 'visivel'> & {
  empresa: Empresa
  alvo?: { setNodeRef: (el: HTMLElement | null) => void; estado: Parameters<typeof estiloDoAlvo>[0] }
}) {
  const { nos, selecao, abertas, onSelecionar } = ramo
  const [aberta, setAberta] = useState(true)
  const ativa = empresa.plataforma
    ? selecao?.tipo === 'raiz' && selecao.organizationId === null
    : selecao?.tipo === 'empresa' && selecao.raizId === empresa.raizId
  const filhos = { ...ramo, visivel: aberta }
  return (
    <NavLink
      ref={alvo?.setNodeRef}
      style={alvo ? estiloDoAlvo(alvo.estado) : undefined}
      label={<Text size="sm" fw={700}>{empresa.nome}</Text>}
      leftSection={<IconBuildings size={16} stroke={1.5} />}
      active={ativa}
      opened={aberta}
      onChange={setAberta}
      onClick={() => onSelecionar(empresa.plataforma
        ? { tipo: 'raiz', organizationId: null }
        : { tipo: 'empresa', raizId: empresa.raizId })}
      childrenOffset={10}
    >
      {empresa.topo.map((o) => <RaizDaOrganizacao key={o.organizationId} raiz={o} {...filhos} />)}
      {empresa.projetos && <NoProjetos projetos={empresa.projetos} {...filhos} />}
      {empresa.pastasDaPlataforma.map((p) => (
        <NoDaPasta key={p.id} no={p} nos={nos} selecao={selecao} abertas={abertas} onSelecionar={onSelecionar} visivel={aberta} />
      ))}
    </NavLink>
  )
}

/** Só a empresa da plataforma registra o alvo: o id dele é único no arraste. */
function EmpresaDaPlataforma(props: Omit<Ramo, 'visivel'> & { empresa: Empresa }) {
  const alvo = useAlvoDeArraste({ tipo: 'raiz', organizationId: null })
  return <NoDaEmpresa {...props} alvo={alvo} />
}

export function ExplorerDePastas({ empresas, nos, selecao, abertas, onSelecionar }: Props) {
  const orgDaSelecao = selecao?.tipo === 'raiz'
    ? selecao.organizationId
    : selecao?.tipo === 'pasta' ? nos.find((n) => n.id === selecao.id)?.organization_id : undefined
  return (
    <Stack gap={2} aria-label="Pastas">
      {empresas.map((e) => {
        const Comp = e.plataforma ? EmpresaDaPlataforma : NoDaEmpresa
        return <Comp key={e.raizId} empresa={e} nos={nos} selecao={selecao} abertas={abertas} onSelecionar={onSelecionar} orgDaSelecao={orgDaSelecao} />
      })}
    </Stack>
  )
}
